/**
 * The intended project for this tab (SB-3a).
 *
 * Boot used to have no *intent*: it read the single global autosave slot and
 * restored whatever had been written last, which is why a tab restored by
 * browser session-restore could come back holding a different project than the
 * one it was showing. This pointer gives boot something to resolve — deep link,
 * then this, then nothing — so the slot it probes is a decision rather than an
 * accident.
 *
 * Principal-scoped for the same reason the autosave slot is: an unscoped
 * pointer on a shared machine hands the next account the previous account's
 * project id, which is a cross-account recovery leak even when the slot itself
 * is scoped.
 *
 * localStorage, not IndexedDB: it is one short string read synchronously on the
 * boot path, and every access is guarded because private modes and
 * storage-blocked contexts throw on the accessor itself.
 */

import { LOCAL_PRINCIPAL_SCOPE } from './state.js';

const KEY_PREFIX = 'pb:last-opened-project';

function keyFor(principalScope) {
  return `${KEY_PREFIX}:${principalScope || LOCAL_PRINCIPAL_SCOPE}`;
}

/**
 * @param {string} principalScope
 * @returns {string|null} the last project this account opened in this browser
 */
export function readLastOpenedProject(principalScope) {
  try {
    return globalThis.localStorage?.getItem(keyFor(principalScope)) || null;
  } catch {
    // Expected flow: private mode / blocked site data. Boot proceeds with no
    // intent, which degrades to the legacy behavior rather than failing.
    return null;
  }
}

/** @param {string} principalScope @param {string|null} projectId */
export function writeLastOpenedProject(principalScope, projectId) {
  try {
    const key = keyFor(principalScope);
    if (projectId) globalThis.localStorage?.setItem(key, String(projectId));
    else globalThis.localStorage?.removeItem(key);
  } catch {
    // Expected flow: see above. A missing pointer costs a less-precise boot,
    // never correctness — the slot key still carries the project id.
  }
}

/** Test seam. */
export function _lastOpenedKeyForTesting(principalScope) {
  return keyFor(principalScope);
}
